// Night Audit — run/preview the end-of-day close and read the Flash Report.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, Moon, Play, Printer } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

const Kpi = ({ label, value, testid }) => (
  <div className="bg-slate-900 text-white rounded-lg px-3 py-2" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-widest text-orange-300">{label}</p>
    <p className="text-xl font-bold leading-tight">{value}</p>
  </div>
);

const ReportView = ({ r }) => {
  const k = r.kpis;
  const cats = Object.entries(r.charges_by_category || {});
  const pays = Object.entries(r.payments_by_method || {});
  const noShowRows = (r.no_shows || []).map((n) => (
    <li key={n.booking_id} className="flex justify-between py-1">
      <span>{n.guest_name} <span className="text-slate-400">· {n.room_name}{n.unit_label ? ` · ${n.unit_label}` : ''} · #{n.booking_id}</span></span>
      <span className="text-slate-600">{eur(n.total_price)}{n.released_in_beds24 ? ' · released' : ''}</span>
    </li>
  ));
  const unpaidRows = (r.unpaid_balances || []).slice(0, 15).map((u) => (
    <li key={u.booking_id} className="flex justify-between py-1">
      <span>{u.guest_name} <span className="text-slate-400">· {u.room_name} · {u.status}</span></span>
      <span className="font-semibold text-red-600">{eur(u.balance)}</span>
    </li>
  ));
  return (
    <div className="bg-white rounded-xl border p-4 space-y-4 print:border-0" data-testid="night-audit-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-slate-900">Manager's Flash Report — {r.business_date}</h3>
          <p className="text-xs text-slate-500">
            {r.dry_run ? 'PREVIEW (nothing written)' : `Run ${String(r.run_at).slice(0, 16).replace('T', ' ')} UTC by ${r.run_by}`}
            {!r.beds24_reachable && ' · ⚠ Beds24 unreachable — occupancy based on local data only'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {r.email && (
            <span className={`px-2 py-1 rounded-full flex items-center gap-1 ${r.email.sent ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-testid="night-audit-email-status">
              <Mail className="w-3 h-3" /> {r.email.sent ? `Emailed to ${r.email.recipient}` : `Not emailed: ${r.email.reason}`}
            </span>
          )}
          <button onClick={() => window.print()} className="px-2 py-1 border rounded-lg flex items-center gap-1 hover:bg-slate-50 print:hidden"><Printer className="w-3 h-3" /> Print</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Occupancy" value={`${k.occupancy_pct}%`} testid="na-kpi-occ" />
        <Kpi label="Rooms sold" value={`${k.rooms_sold} / ${k.total_units}`} testid="na-kpi-sold" />
        <Kpi label="ADR" value={eur(k.adr)} testid="na-kpi-adr" />
        <Kpi label="RevPAR" value={eur(k.revpar)} testid="na-kpi-revpar" />
        <Kpi label="Room revenue" value={eur(k.room_revenue)} testid="na-kpi-roomrev" />
        <Kpi label="Payments taken" value={eur(k.total_payments)} testid="na-kpi-pay" />
        <Kpi label="Arrivals / Departures" value={`${k.arrivals} / ${k.departures}`} testid="na-kpi-arrdep" />
        <Kpi label="No-shows" value={k.no_shows} testid="na-kpi-noshow" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div>
          <h4 className="font-semibold text-slate-800 mb-1">Revenue by category</h4>
          {cats.length ? cats.map(([c, v]) => <div key={c} className="flex justify-between py-0.5"><span className="capitalize">{c.replace('_', ' ')}</span><b>{eur(v)}</b></div>) : <p className="text-slate-400">No charges posted this day</p>}
        </div>
        <div>
          <h4 className="font-semibold text-slate-800 mb-1">Payments by method</h4>
          {pays.length ? pays.map(([m, v]) => <div key={m} className="flex justify-between py-0.5"><span className="capitalize">{m}</span><b>{eur(v)}</b></div>) : <p className="text-slate-400">No payments this day</p>}
        </div>
        <div>
          <h4 className="font-semibold text-slate-800 mb-1">No-shows ({r.no_shows?.length || 0})</h4>
          {noShowRows.length ? <ul className="divide-y">{noShowRows}</ul> : <p className="text-slate-400">None</p>}
        </div>
        <div>
          <h4 className="font-semibold text-slate-800 mb-1">Unpaid balances — {eur(k.unpaid_total)} · {k.unpaid_count} folios</h4>
          {unpaidRows.length ? <ul className="divide-y">{unpaidRows}</ul> : <p className="text-slate-400">Nothing outstanding</p>}
        </div>
      </div>
      <p className="text-xs text-slate-500">{(r.room_charges_posted || []).length} room nights {r.dry_run ? 'would be' : ''} posted to in-house folios.</p>
    </div>
  );
};

const NightAuditTab = ({ canRun }) => {
  const [reports, setReports] = useState([]);
  const [bizDate, setBizDate] = useState('');
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/admin/finance/night-audit/reports?limit=30');
      setReports(r.reports || []);
      setBizDate((d) => d || r.suggested_business_date);
      if (r.reports?.length && !current) {
        const full = await apiCall(`/api/admin/finance/night-audit/reports/${r.reports[0].business_date}`);
        setCurrent(full);
      }
    } catch (e) { setErr(e.message); }
  }, [current]);
  useEffect(() => { load(); }, [load]);

  const run = async (dryRun) => {
    setBusy(true); setErr('');
    try {
      const r = await apiCall('/api/admin/finance/night-audit/run', {
        method: 'POST', body: JSON.stringify({ business_date: bizDate, dry_run: dryRun }),
      });
      setCurrent(r);
      if (!dryRun) load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const open = async (d) => {
    try { setCurrent(await apiCall(`/api/admin/finance/night-audit/reports/${d}`)); } catch (e) { setErr(e.message); }
  };

  const history = reports.map((r) => (
    <button key={r.business_date} onClick={() => open(r.business_date)}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between hover:bg-slate-50 ${current?.business_date === r.business_date && !current?.dry_run ? 'bg-orange-50 border border-orange-200' : 'border border-transparent'}`}
      data-testid={`night-audit-history-${r.business_date}`}>
      <span className="font-medium">{r.business_date}</span>
      <span className="text-slate-500">{r.kpis?.occupancy_pct}% · ADR {eur(r.kpis?.adr)}</span>
    </button>
  ));

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      <div className="space-y-4">
        {canRun && (
          <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="night-audit-run-card">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Moon className="w-4 h-4 text-orange-500" /> Close business day</h3>
            <label className="block text-xs text-slate-600">Business date
              <input type="date" value={bizDate} onChange={(e) => setBizDate(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="night-audit-date" />
            </label>
            <div className="flex gap-2">
              <button onClick={() => run(true)} disabled={busy || !bizDate} className="flex-1 px-3 py-2 rounded-lg border text-sm font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="night-audit-preview">
                Preview
              </button>
              <button onClick={() => run(false)} disabled={busy || !bizDate} className="flex-1 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 flex items-center justify-center gap-1 disabled:opacity-50" data-testid="night-audit-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run audit
              </button>
            </div>
            <p className="text-[11px] text-slate-500">Posts tonight's room charge to every checked-in folio, flags confirmed arrivals that never checked in as no-shows, and emails the Flash Report. Safe to re-run.</p>
            {err && <p className="text-xs text-red-600" data-testid="night-audit-error">{err}</p>}
          </div>
        )}
        <div className="bg-white rounded-xl border p-3">
          <h3 className="font-semibold text-sm px-1 mb-2">Closed days</h3>
          {history.length ? <div className="space-y-1 max-h-[420px] overflow-y-auto">{history}</div> : <p className="text-xs text-slate-400 px-1">No audits yet — run your first close.</p>}
        </div>
      </div>
      <div>
        {current ? <ReportView r={current} /> : (
          <div className="bg-white rounded-xl border p-10 text-center text-slate-400 text-sm">Pick a closed day or run the audit to see the Flash Report.</div>
        )}
      </div>
    </div>
  );
};

export default NightAuditTab;
